import { ChevronDown, Eye, EyeOff, ShieldCheck, X } from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api, ApiError, csrfToken } from "@/api";
import type { AuthDialogIntent, AuthProvider } from "@/session/SessionProvider";
import {
  providerAuthErrorMessage,
  safeProviderLoginUrl,
  useSession,
} from "@/session/SessionProvider";
import { walletAvailable } from "@/session/walletAuth";

type Mode = AuthDialogIntent;

const TRUST_MARKS = [
  "applepay",
  "googlepay",
  "visacard",
  "mastercard",
  "usdt",
  "btc",
  "eth",
  "usdc",
] as const;

const PROVIDERS = [
  { id: "google", label: "Google", kind: "oauth" },
  { id: "apple", label: "Apple", kind: "oauth" },
  { id: "discord", label: "Discord", kind: "oauth" },
  { id: "facebook", label: "Facebook", kind: "oauth" },
  { id: "metamask", label: "MetaMask", kind: "wallet" },
  { id: "phantom", label: "Phantom", kind: "wallet" },
] as const;

function providerReason(
  code: string | null | undefined,
  isNative = false,
): string {
  switch (code) {
    case "PROVIDER_NOT_CONFIGURED":
    case "PROVIDER_UNCONFIGURED":
    case "PROVIDER_ALIAS_UNCONFIGURED":
    case "PROVIDER_BRIDGE_UNCONFIGURED":
    case "NOT_CONFIGURED":
      return "Not configured for this environment.";
    case "PROVIDER_CALLBACK_UNVERIFIED":
      return "Awaiting provider callback approval for Tilt Racing.";
    case "PROVIDER_DISABLED":
    case "DISABLED":
      return "Not enabled by this operator.";
    case "PROVIDER_READ_ONLY":
    case "READ_ONLY":
      return "Provider configuration is read-only here.";
    case "PROVIDER_MFA_REQUIRED":
    case "PROVIDER_MFA_POLICY_UNVERIFIED":
    case "MFA_REQUIRED":
      return isNative
        ? "Complete account security checks using secure login on the Tilt Racing website."
        : "Use secure login to complete your account security checks.";
    default:
      return "Not available in this environment. Use account sign-in instead.";
  }
}

function detailErrors(error: ApiError): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(error.details)) {
    if (Array.isArray(value) && value.length) result[key] = String(value[0]);
    else if (typeof value === "string") result[key] = value;
    else if (value && typeof value === "object") {
      result[key] = Object.values(value as Record<string, unknown>)
        .flat()
        .map(String)
        .join(" ");
    }
  }
  return result;
}

export function postAuthDestination(pathname: string, search: string) {
  if (pathname === "/app" || pathname.startsWith("/app/")) {
    return `${pathname}${search}`;
  }
  return "/app/";
}

export function AuthDialog({ onClose }: { onClose: () => void }) {
  const {
    authMode,
    login,
    loginUrl,
    directLogin,
    loginIntent,
    register,
    registrationEnabled,
    loading,
    providers = [],
    providersLoading,
    providersError,
    pendingProvider,
    refreshProviders,
    providerPending,
    isNative,
    nativeProviderAuthAvailable,
    nativeAppleAuthAvailable,
    authCallbackError,
    clearAuthCallbackError,
    authenticateProvider,
  } = useSession();
  const [mode, setMode] = useState<Mode>(() =>
    loginIntent === "register" && registrationEnabled ? "register" : "login",
  );
  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState<
    Array<{ code: string; name: string }>
  >([]);
  const [referralCode, setReferralCode] = useState("");
  const [referralOpen, setReferralOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [totp, setTotp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [optionsReady, setOptionsReady] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [optionsAttempt, setOptionsAttempt] = useState(0);
  const [usernameEditable, setUsernameEditable] = useState(false);
  const [providerError, setProviderError] = useState("");
  const [unlinkedProvider, setUnlinkedProvider] = useState("");
  const firstInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  const returnFocusRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  const passwordRules = useMemo(
    () => ({
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /\d/.test(password),
    }),
    [password],
  );
  const passwordStrong = Object.values(passwordRules).every(Boolean);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const registrationReady =
    registrationEnabled &&
    optionsReady &&
    emailValid &&
    passwordStrong &&
    Boolean(username.trim()) &&
    countries.some((item) => item.code === country);

  useEffect(() => {
    (firstInputRef.current ?? dialogRef.current)?.focus();
  }, [mode, mfaRequired, authMode]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const returnFocus = returnFocusRef.current;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      const focusable = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          "button, a[href], input, select, [tabindex]",
        ) ?? [],
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.hasAttribute("disabled") &&
          element.getAttribute("type") !== "hidden" &&
          !element.closest('[hidden], [inert], [aria-hidden="true"]'),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !dialog?.contains(document.activeElement) ||
          document.activeElement === dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !dialog?.contains(document.activeElement) ||
          document.activeElement === dialog)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, []);

  useEffect(() => {
    if (mode !== "register" || authMode !== "direct" || optionsReady) return;
    let active = true;
    void Promise.resolve()
      .then(() => {
        if (active) {
          setOptionsLoading(true);
          setOptionsError("");
        }
        return api.registrationOptions();
      })
      .then((options) => {
        if (!active) return;
        if (!options.countries.length)
          throw new Error("No registration countries configured.");
        setCountries(options.countries);
        setUsername(options.suggestedUsername ?? "");
        setUsernameEditable(!options.suggestedUsername);
        setCountry(
          options.countries.some(
            (item) => item.code === options.detectedCountry,
          )
            ? options.detectedCountry
            : "",
        );
        setOptionsLoading(false);
        setOptionsReady(true);
      })
      .catch(() => {
        if (active)
          setOptionsError(
            "Account setup is temporarily unavailable. Please retry.",
          );
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authMode, mode, optionsReady, optionsAttempt]);

  function resetErrors() {
    clearAuthCallbackError();
    setProviderError("");
    setError("");
    setSuccess("");
    setFieldErrors({});
  }

  function changeMode(next: Mode) {
    if (next === "register" && !registrationEnabled) return;
    setMode(next);
    setMfaRequired(false);
    setTotp("");
    setShowPassword(false);
    resetErrors();
  }

  async function submitDev(event: FormEvent) {
    event.preventDefault();
    if (!identifier.trim()) return;
    setSubmitting(true);
    resetErrors();
    try {
      await login(identifier.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitLogin(event: FormEvent) {
    event.preventDefault();
    if (providerPending || submitting || !identifier.trim() || !password)
      return;
    setSubmitting(true);
    resetErrors();
    try {
      await directLogin({
        username: identifier.trim(),
        password,
        rememberMe,
        ...(mfaRequired ? { totp } : {}),
      });
    } catch (caught) {
      if (caught instanceof ApiError && caught.details.mfaRequired === true) {
        setMfaRequired(true);
        setError("");
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : "Invalid username or password.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRegistration(event: FormEvent) {
    event.preventDefault();
    resetErrors();
    if (!passwordStrong) {
      setFieldErrors({
        password:
          "Use at least 8 characters with uppercase, lowercase, and a number.",
      });
      return;
    }
    if (providerPending || submitting || !registrationReady) return;
    setSubmitting(true);
    try {
      const result = await register({
        email: email.trim().toLowerCase(),
        username: username.trim(),
        password,
        // MonkeyTilt's current modal has one password field. HRW's proxy
        // retains its confirmation guard by deriving the identical value.
        passwordConfirm: password,
        country,
        referralCode: referralCode.trim(),
        isLegalAge: true,
        isTermsAccepted: true,
        isPrivacyAccepted: true,
        // Current core defaults email/promo consent on; users can opt out in
        // account settings and every email retains unsubscribe controls.
        isEmailAllowed: true,
        welcomeBonusOptIn: true,
      });
      if (result.accountCreated && !result.session) {
        setMode("login");
        setIdentifier(username);
        setSuccess(result.message ?? "Account created. Sign in to continue.");
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        const errors = detailErrors(caught);
        setFieldErrors(errors);
        if (errors.username) setUsernameEditable(true);
      }
      setError(
        caught instanceof Error ? caught.message : "Registration failed.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const stagedProviderLabel =
    unlinkedProvider ||
    PROVIDERS.find((provider) => provider.id === pendingProvider)?.label;
  const destination = postAuthDestination(
    window.location.pathname,
    window.location.search,
  );
  const secureProvider: AuthProvider = {
    id: "keycloak",
    label: "MonkeyTilt",
    kind: "oauth",
    available: Boolean(loginUrl),
    reasonCode: null,
    ...(loginUrl ? { loginUrl } : {}),
  };
  const secureUrl = safeProviderLoginUrl(secureProvider);

  async function startProvider(provider: AuthProvider) {
    clearAuthCallbackError();
    setProviderError("");
    setUnlinkedProvider("");
    try {
      const result = await authenticateProvider(provider, destination);
      if (result.status === "unlinked") setUnlinkedProvider(provider.label);
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        ["PROVIDER_UNLINKED", "PROVIDER_NOT_LINKED"].includes(caught.code)
      ) {
        setUnlinkedProvider(provider.label);
      } else {
        const code =
          caught && typeof caught === "object" && "code" in caught
            ? caught.code
            : caught instanceof Error
              ? caught.message
              : caught;
        setProviderError(providerAuthErrorMessage(code, isNative));
      }
    }
  }

  const providerGuidance = stagedProviderLabel ? (
    <div className="auth-provider-guidance" role="status">
      <p>
        {stagedProviderLabel} is verified but is not connected to a MonkeyTilt
        account. No account has been signed in or created.
      </p>
      <p>
        Already have an account? Sign in with your existing credentials.
        Connecting this provider is a separate, explicit step; sign-in or
        registration alone does not link it. We never match accounts by email.
      </p>
      {authMode === "direct" ? (
        <>
          <button
            className="auth-btn-secondary"
            onClick={() => changeMode("login")}
            type="button"
          >
            Sign in to an existing account
          </button>
          {registrationEnabled ? (
            <button
              className="auth-btn-secondary"
              onClick={() => changeMode("register")}
              type="button"
            >
              Register a new account, then connect
            </button>
          ) : null}
        </>
      ) : (
        <p>
          {isNative
            ? "Sign in to your existing account on the Tilt Racing website; brokered secure login is not supported in this app."
            : "Use secure login below to sign in to your existing account."}
        </p>
      )}
    </div>
  ) : null;

  const fallback = secureUrl ? (
    <form
      action={secureUrl}
      className="auth-secure-fallback"
      method="post"
      onSubmit={(event) => {
        // Brokered Keycloak is not a supported native provider. Never route
        // this form through generic native OAuth or submit it in the WebView.
        if (isNative) event.preventDefault();
      }}
    >
      <input name="csrfmiddlewaretoken" type="hidden" value={csrfToken()} />
      <input name="next" type="hidden" value={destination} />
      <button
        className="auth-btn-secondary"
        disabled={submitting || providerPending || isNative}
        type="submit"
      >
        Use MonkeyTilt secure login
      </button>
      <small>
        {isNative
          ? "Brokered secure login is not supported in this app. Use your account credentials, or sign in on the Tilt Racing website."
          : "Passkeys or accounts requiring another secure action"}
      </small>
    </form>
  ) : null;

  return (
    <div
      className="modal-backdrop auth-modal-overlay active"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && window.innerWidth < 768) {
          onClose();
        }
      }}
      role="presentation"
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        aria-labelledby="auth-heading"
        aria-modal="true"
        className="modal auth-dialog auth-modal auth-modal-bg"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <button
          aria-label="Close"
          className="auth-close-btn"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden />
        </button>

        <div className="auth-modal-inner" data-auth-mobile-mode="sell">
          {authMode === "direct" && !mfaRequired ? (
            <AuthTabs
              mode={mode}
              registrationEnabled={registrationEnabled}
              setMode={changeMode}
            />
          ) : null}

          <div className="auth-modal-content">
            <div className="auth-modal-body">
              {providerError || authCallbackError ? (
                <p className="auth-error" role="alert">
                  {providerError || authCallbackError}
                </p>
              ) : null}
              {authMode === "direct" ? (
                <div
                  className="auth-tab-content active auth-unified-form"
                  data-mode={mode}
                >
                  {mfaRequired ? (
                    <MfaForm
                      error={error}
                      firstInputRef={firstInputRef}
                      onBack={() => {
                        setMfaRequired(false);
                        setTotp("");
                        resetErrors();
                      }}
                      onSubmit={submitLogin}
                      setTotp={setTotp}
                      submitting={Boolean(submitting || providerPending)}
                      totp={totp}
                    />
                  ) : (
                    <>
                      <AuthHeadline mode={mode} />
                      <TrustMarks />
                      {mode === "login" ? (
                        <LoginForm
                          error={error}
                          firstInputRef={firstInputRef}
                          identifier={identifier}
                          onSubmit={submitLogin}
                          password={password}
                          rememberMe={rememberMe}
                          setIdentifier={setIdentifier}
                          setPassword={setPassword}
                          setRememberMe={setRememberMe}
                          setShowPassword={setShowPassword}
                          showPassword={showPassword}
                          submitting={Boolean(submitting || providerPending)}
                          success={success}
                        />
                      ) : (
                        <RegisterForm
                          countries={countries}
                          country={country}
                          email={email}
                          error={error}
                          fieldErrors={fieldErrors}
                          firstInputRef={firstInputRef}
                          onSubmit={submitRegistration}
                          optionsLoading={optionsLoading}
                          optionsError={optionsError}
                          retryOptions={() =>
                            setOptionsAttempt((attempt) => attempt + 1)
                          }
                          username={username}
                          usernameEditable={usernameEditable}
                          editUsername={() => setUsernameEditable(true)}
                          setUsername={setUsername}
                          password={password}
                          ready={registrationReady}
                          referralCode={referralCode}
                          referralOpen={referralOpen}
                          setCountry={setCountry}
                          setEmail={setEmail}
                          setPassword={setPassword}
                          setReferralCode={setReferralCode}
                          setReferralOpen={setReferralOpen}
                          setShowPassword={setShowPassword}
                          showPassword={showPassword}
                          submitting={Boolean(submitting || providerPending)}
                        />
                      )}
                      <ProviderButtons
                        providers={providers}
                        loading={Boolean(providersLoading)}
                        error={providersError}
                        retry={() => void refreshProviders()}
                        pending={Boolean(providerPending || submitting)}
                        isNative={Boolean(isNative)}
                        nativeAvailable={Boolean(nativeProviderAuthAvailable)}
                        nativeAppleAvailable={Boolean(nativeAppleAuthAvailable)}
                        destination={destination}
                        startProvider={startProvider}
                      />
                      {providerGuidance}
                      <TermsNotice />
                      <BrandLogo />
                      {fallback}
                    </>
                  )}
                </div>
              ) : authMode === "dev" ? (
                <DevForm
                  error={error}
                  firstInputRef={firstInputRef}
                  identifier={identifier}
                  onClose={onClose}
                  onSubmit={submitDev}
                  setIdentifier={setIdentifier}
                  submitting={Boolean(submitting || providerPending)}
                />
              ) : authMode === "oidc" && fallback ? (
                <div className="auth-standalone-state">
                  <AuthHeadline mode="login" />
                  <p>Continue with your MonkeyTilt account.</p>
                  {providerGuidance}
                  {fallback}
                  <ProviderButtons
                    providers={providers}
                    loading={Boolean(providersLoading)}
                    error={providersError}
                    retry={() => void refreshProviders()}
                    pending={Boolean(providerPending)}
                    isNative={Boolean(isNative)}
                    nativeAvailable={Boolean(nativeProviderAuthAvailable)}
                    nativeAppleAvailable={Boolean(nativeAppleAuthAvailable)}
                    destination={destination}
                    startProvider={startProvider}
                  />
                  <BrandLogo />
                </div>
              ) : (
                <div className="auth-standalone-state">
                  <AuthHeadline mode="login" />
                  <p role="status">
                    {loading
                      ? "Loading account sign-in…"
                      : "Operator login is not configured for this environment. No account has been created."}
                  </p>
                  <button
                    className="auth-btn-secondary"
                    onClick={onClose}
                    type="button"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function AuthTabs({
  mode,
  registrationEnabled,
  setMode,
}: {
  mode: Mode;
  registrationEnabled: boolean;
  setMode: (mode: Mode) => void;
}) {
  return (
    <div className="auth-modal-tabs">
      <div className="auth-tabs-with-bg">
        <div className="auth-tabs-grid" role="tablist">
          <div
            aria-hidden
            className="auth-tabs-indicator"
            data-position={mode}
          />
          <button
            aria-selected={mode === "login"}
            className="auth-tab-btn"
            data-active={mode === "login"}
            onClick={() => setMode("login")}
            role="tab"
            type="button"
          >
            Sign In
          </button>
          <button
            aria-selected={mode === "register"}
            className="auth-tab-btn"
            data-active={mode === "register"}
            disabled={!registrationEnabled}
            onClick={() => setMode("register")}
            role="tab"
            type="button"
          >
            Register
          </button>
        </div>
      </div>
    </div>
  );
}

function AuthHeadline({ mode }: { mode: Mode }) {
  return (
    <div className="auth-headline-section">
      <h2 className="auth-heading-script" id="auth-heading">
        {mode === "login" ? "Welcome Back" : "Join 250,000+ Players"}
      </h2>
    </div>
  );
}

function TrustMarks() {
  return (
    <div aria-label="Supported payment methods" className="auth-trust-row">
      {TRUST_MARKS.map((mark, index) => (
        <span className="auth-trust-mark" key={mark}>
          {index === 4 ? <i aria-hidden /> : null}
          <img
            alt=""
            src={`/app/static/themes/monkeytilt/auth/trust/${mark}.svg`}
          />
        </span>
      ))}
    </div>
  );
}

function LoginForm({
  error,
  firstInputRef,
  identifier,
  onSubmit,
  password,
  rememberMe,
  setIdentifier,
  setPassword,
  setRememberMe,
  setShowPassword,
  showPassword,
  submitting,
  success,
}: {
  error: string;
  firstInputRef: RefObject<HTMLInputElement | null>;
  identifier: string;
  onSubmit: (event: FormEvent) => Promise<void>;
  password: string;
  rememberMe: boolean;
  setIdentifier: (value: string) => void;
  setPassword: (value: string) => void;
  setRememberMe: (value: boolean) => void;
  setShowPassword: (value: boolean) => void;
  showPassword: boolean;
  submitting: boolean;
  success: string;
}) {
  return (
    <form
      className="auth-unified-fields"
      onSubmit={(event) => void onSubmit(event)}
    >
      <AuthField label="Email or Username">
        <input
          ref={firstInputRef}
          aria-label="Email or username"
          autoComplete="username"
          maxLength={254}
          onChange={(event) => setIdentifier(event.target.value)}
          placeholder="Email or username"
          required
          value={identifier}
        />
      </AuthField>
      <PasswordField
        autoComplete="current-password"
        onChange={setPassword}
        placeholder="Enter password"
        show={showPassword}
        toggle={() => setShowPassword(!showPassword)}
        value={password}
      />
      <div className="auth-mode-swap">
        <div className="auth-login-options">
          <label className="auth-checkbox-wrapper">
            <input
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              type="checkbox"
            />
            <span>Remember me</span>
          </label>
          <a
            className="auth-btn-text"
            href="https://monkeytilt.com/casino/?auth=reset"
            rel="noreferrer"
            target="_blank"
          >
            Forgot password?
          </a>
        </div>
      </div>
      {success ? (
        <p className="auth-success" role="status">
          {success}
        </p>
      ) : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      <button
        className="auth-btn-primary auth-btn-gradient"
        disabled={submitting || !identifier.trim() || !password}
        type="submit"
      >
        {submitting ? <Spinner /> : "SIGN IN"}
      </button>
    </form>
  );
}

function RegisterForm({
  optionsLoading,
  optionsError,
  retryOptions,
  username,
  usernameEditable,
  editUsername,
  setUsername,
  countries,
  country,
  email,
  error,
  fieldErrors,
  firstInputRef,
  onSubmit,
  password,
  ready,
  referralCode,
  referralOpen,
  setCountry,
  setEmail,
  setPassword,
  setReferralCode,
  setReferralOpen,
  setShowPassword,
  showPassword,
  submitting,
}: {
  optionsLoading: boolean;
  optionsError: string;
  retryOptions: () => void;
  username: string;
  usernameEditable: boolean;
  editUsername: () => void;
  setUsername: (value: string) => void;
  countries: Array<{ code: string; name: string }>;
  country: string;
  email: string;
  error: string;
  fieldErrors: Record<string, string>;
  firstInputRef: RefObject<HTMLInputElement | null>;
  onSubmit: (event: FormEvent) => Promise<void>;
  password: string;
  ready: boolean;
  referralCode: string;
  referralOpen: boolean;
  setCountry: (value: string) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setReferralCode: (value: string) => void;
  setReferralOpen: (value: boolean) => void;
  setShowPassword: (value: boolean) => void;
  showPassword: boolean;
  submitting: boolean;
}) {
  return (
    <form
      className="auth-unified-fields"
      onSubmit={(event) => void onSubmit(event)}
    >
      {optionsLoading ? (
        <p className="auth-provider-status" role="status">
          Loading account setup…
        </p>
      ) : null}
      {optionsError ? (
        <div className="auth-provider-status">
          <p role="alert">{optionsError}</p>
          <button
            className="auth-btn-secondary"
            onClick={retryOptions}
            type="button"
          >
            Retry account setup
          </button>
        </div>
      ) : null}
      <AuthField error={fieldErrors.email} label="Email">
        <input
          ref={firstInputRef}
          aria-label="Email"
          autoComplete="email"
          maxLength={254}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Enter your email"
          required
          type="email"
          value={email}
        />
      </AuthField>
      <PasswordField
        autoComplete="new-password"
        error={fieldErrors.password}
        onChange={setPassword}
        placeholder="Create a password"
        show={showPassword}
        toggle={() => setShowPassword(!showPassword)}
        value={password}
      />
      <p className="auth-password-guidance" id="auth-password-guidance">
        Use at least 8 characters with uppercase, lowercase, and a number.
      </p>
      {usernameEditable ? (
        <AuthField error={fieldErrors.username} label="Username">
          <input
            aria-label="Username"
            aria-invalid={Boolean(fieldErrors.username)}
            autoComplete="username"
            maxLength={150}
            onChange={(event) => setUsername(event.target.value)}
            required
            value={username}
          />
        </AuthField>
      ) : username ? (
        <div className="auth-generated-username">
          <span>
            Your username: <strong>{username}</strong>
          </span>
          <button
            className="auth-btn-text"
            onClick={editUsername}
            type="button"
          >
            Edit username
          </button>
        </div>
      ) : null}
      {!country && countries.length ? (
        <p className="auth-provider-status">
          We could not detect your country. Select it below to continue.
        </p>
      ) : null}
      {countries.length ? (
        <AuthField error={fieldErrors.country} label="Country">
          <select
            aria-label="Country"
            onChange={(event) => setCountry(event.target.value)}
            required
            value={country}
          >
            <option value="">Select your country</option>
            {countries.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </AuthField>
      ) : null}
      <div className="auth-mode-swap">
        <div className="auth-referral-field">
          <button
            aria-expanded={referralOpen}
            className="auth-referral-toggle"
            onClick={() => setReferralOpen(!referralOpen)}
            type="button"
          >
            <span>Have a referral code?</span>
            <ChevronDown aria-hidden />
          </button>
          {referralOpen ? (
            <div className="auth-referral-input">
              <input
                aria-label="Referral code"
                autoComplete="off"
                maxLength={64}
                onChange={(event) => setReferralCode(event.target.value)}
                placeholder="Enter referral code"
                value={referralCode}
              />
            </div>
          ) : null}
        </div>
      </div>
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      <button
        className="auth-btn-primary auth-btn-gradient"
        disabled={submitting || !ready}
        type="submit"
      >
        {submitting ? <Spinner /> : "CREATE ACCOUNT"}
      </button>
    </form>
  );
}

function AuthField({
  children,
  error,
  label,
}: {
  children: ReactNode;
  error?: string;
  label: string;
}) {
  return (
    <label className="auth-field">
      <span>{label}</span>
      {children}
      {error ? <small className="auth-field-error">{error}</small> : null}
    </label>
  );
}

function PasswordField({
  value,
  onChange,
  show,
  toggle,
  autoComplete,
  error,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  toggle: () => void;
  autoComplete: string;
  error?: string;
  placeholder: string;
}) {
  const inputId = `auth-password-${autoComplete}`;
  return (
    <div className="auth-field">
      <label htmlFor={inputId}>Password</label>
      <div className="auth-password-wrapper">
        <input
          aria-label="Password"
          aria-describedby={
            autoComplete === "new-password"
              ? "auth-password-guidance"
              : undefined
          }
          aria-invalid={Boolean(error)}
          autoComplete={autoComplete}
          id={inputId}
          maxLength={1024}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          required
          type={show ? "text" : "password"}
          value={value}
        />
        <button
          aria-label={show ? "Hide password" : "Show password"}
          className="auth-password-toggle"
          onClick={toggle}
          type="button"
        >
          {show ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
        </button>
      </div>
      {error ? <small className="auth-field-error">{error}</small> : null}
    </div>
  );
}

function ProviderButtons({
  providers,
  loading,
  error,
  retry,
  pending,
  isNative,
  nativeAvailable,
  nativeAppleAvailable,
  destination,
  startProvider,
}: {
  providers: AuthProvider[];
  loading: boolean;
  error?: string;
  retry: () => void;
  pending: boolean;
  isNative: boolean;
  nativeAvailable: boolean;
  nativeAppleAvailable: boolean;
  destination: string;
  startProvider: (provider: AuthProvider) => Promise<void>;
}) {
  return (
    <div className="auth-social-section" aria-busy={loading || pending}>
      <div className="auth-divider">
        <i aria-hidden />
        <span>Or continue with</span>
        <i aria-hidden />
      </div>
      {loading ? (
        <p className="auth-provider-status" role="status">
          Checking available sign-in methods…
        </p>
      ) : null}
      {error ? (
        <div className="auth-provider-status">
          <p role="alert">{error}</p>
          <button className="auth-btn-secondary" onClick={retry} type="button">
            Retry sign-in methods
          </button>
        </div>
      ) : null}
      <div className="auth-social-buttons">
        {PROVIDERS.map((item) => {
          const matches = providers.filter(
            (provider) =>
              provider.id === item.id && provider.kind === item.kind,
          );
          const provider = matches.length === 1 ? matches[0] : undefined;
          const loginUrl = provider ? safeProviderLoginUrl(provider) : null;
          let reason = loading
            ? "Checking availability…"
            : error
              ? "Availability could not be checked."
              : !provider?.available
                ? providerReason(provider?.reasonCode, isNative)
                : "";
          if (!reason && item.kind === "oauth" && !loginUrl)
            reason = "Secure sign-in is not configured correctly.";
          const nativeReady =
            nativeAvailable ||
            (item.id === "apple" &&
              provider?.nativeAppleAvailable &&
              nativeAppleAvailable);
          if (!reason && item.kind === "oauth" && isNative && !nativeReady)
            reason = "Unavailable in this app build. Use account sign-in.";
          if (
            !reason &&
            (item.id === "metamask" || item.id === "phantom") &&
            !walletAvailable(item.id)
          )
            reason = `Open in a browser with ${item.label} installed and unlocked.`;
          const disabled = Boolean(reason || pending);
          const content = (
            <>
              {!["apple", "facebook"].includes(item.id) ? (
                <img
                  alt=""
                  src={`/app/static/themes/monkeytilt/auth/${item.id}.svg`}
                />
              ) : (
                <span aria-hidden className="auth-provider-monogram">
                  {item.id === "apple" ? "A" : "f"}
                </span>
              )}
              <span>{item.label}</span>
            </>
          );
          return (
            <div className="auth-provider-option" key={item.id}>
              {item.kind === "oauth" && loginUrl && !isNative ? (
                <form action={loginUrl} method="post">
                  <input
                    name="csrfmiddlewaretoken"
                    type="hidden"
                    value={csrfToken()}
                  />
                  <input name="next" type="hidden" value={destination} />
                  <button
                    aria-describedby={
                      reason ? `auth-provider-${item.id}-reason` : undefined
                    }
                    aria-label={`Continue with ${item.label}`}
                    className="auth-social-btn"
                    disabled={disabled}
                    type="submit"
                  >
                    {content}
                  </button>
                </form>
              ) : (
                <button
                  aria-describedby={
                    reason ? `auth-provider-${item.id}-reason` : undefined
                  }
                  aria-label={`Continue with ${item.label}`}
                  className="auth-social-btn"
                  disabled={disabled}
                  onClick={() => {
                    if (provider) void startProvider(provider);
                  }}
                  type="button"
                >
                  {content}
                </button>
              )}
              {reason ? (
                <small id={`auth-provider-${item.id}-reason`}>{reason}</small>
              ) : null}
            </div>
          );
        })}
      </div>
      {pending ? (
        <p className="auth-provider-status" role="status">
          Complete sign-in in the secure window or your wallet. You can cancel
          there.
        </p>
      ) : null}
    </div>
  );
}

function TermsNotice() {
  return (
    <p className="auth-terms-acceptance">
      By continuing, I confirm I am 18+ and agree to the{" "}
      <a
        href="https://www.monkeytilt.com/terms"
        rel="noreferrer"
        target="_blank"
      >
        Terms of Service
      </a>{" "}
      and{" "}
      <a
        href="https://www.monkeytilt.com/privacy"
        rel="noreferrer"
        target="_blank"
      >
        Privacy Policy
      </a>
      .
    </p>
  );
}

function BrandLogo() {
  return (
    <div className="auth-brand-logo">
      <img
        alt="Monkey Tilt"
        draggable={false}
        src="/app/static/themes/monkeytilt/logo-wordmark.webp"
      />
    </div>
  );
}

function MfaForm({
  error,
  firstInputRef,
  onBack,
  onSubmit,
  setTotp,
  submitting,
  totp,
}: {
  error: string;
  firstInputRef: RefObject<HTMLInputElement | null>;
  onBack: () => void;
  onSubmit: (event: FormEvent) => Promise<void>;
  setTotp: (value: string) => void;
  submitting: boolean;
  totp: string;
}) {
  return (
    <div className="auth-mfa-view">
      <div className="auth-mfa-heading">
        <ShieldCheck aria-hidden />
        <h2 id="auth-heading">Verify Your Sign In</h2>
        <p>Enter the code from your authenticator.</p>
      </div>
      <form
        className="auth-unified-fields"
        onSubmit={(event) => void onSubmit(event)}
      >
        <AuthField label="One-time code">
          <input
            ref={firstInputRef}
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={8}
            onChange={(event) => setTotp(event.target.value.replace(/\D/g, ""))}
            pattern="\d{6,8}"
            required
            value={totp}
          />
        </AuthField>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="auth-btn-primary auth-btn-gradient"
          disabled={submitting || !/^\d{6,8}$/.test(totp)}
          type="submit"
        >
          {submitting ? <Spinner /> : "VERIFY CODE"}
        </button>
        <button
          className="auth-btn-text auth-mfa-back"
          onClick={onBack}
          type="button"
        >
          Back to sign in
        </button>
      </form>
      <BrandLogo />
    </div>
  );
}

function DevForm({
  error,
  firstInputRef,
  identifier,
  onClose,
  onSubmit,
  setIdentifier,
  submitting,
}: {
  error: string;
  firstInputRef: RefObject<HTMLInputElement | null>;
  identifier: string;
  onClose: () => void;
  onSubmit: (event: FormEvent) => Promise<void>;
  setIdentifier: (value: string) => void;
  submitting: boolean;
}) {
  return (
    <div className="auth-standalone-state">
      <h2 className="auth-heading-script" id="auth-heading">
        Enter the World
      </h2>
      <p>Pick a stable name for this development session.</p>
      <form
        className="auth-unified-fields"
        onSubmit={(event) => void onSubmit(event)}
      >
        <AuthField label="Stable name">
          <input
            ref={firstInputRef}
            autoComplete="nickname"
            maxLength={32}
            onChange={(event) => setIdentifier(event.target.value)}
            placeholder="e.g. Meridian Racing"
            value={identifier}
          />
        </AuthField>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="auth-btn-primary auth-btn-gradient"
          disabled={submitting || !identifier.trim()}
          type="submit"
        >
          {submitting ? <Spinner /> : "Enter stable"}
        </button>
        <button className="auth-btn-text" onClick={onClose} type="button">
          Close development sign in
        </button>
      </form>
      <BrandLogo />
    </div>
  );
}

function Spinner() {
  return <span aria-label="Working" className="auth-spinner" role="status" />;
}
